import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  QrCode, 
  Smartphone, 
  BarChart3, 
  Coffee, 
  ArrowRight,
  Star,
  Users,
  Zap,
  Shield,
  Sparkles,
  ChevronDown
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import TransparentHeader from '../components/TransparentHeader';
import '../styles/video.css';
import '../styles/landing.css';

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-orange-400/20 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  );
};

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={countRef} className="font-bold text-4xl md:text-5xl bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
      {count}{suffix}
    </span>
  );
};

// Hero section with enhanced video integration
const EnhancedHeroSection = ({ isAuthenticated, user, logout }) => {
  // Removed unused translation function 't' (using direct i18n language checks instead)
  const { i18n } = useTranslation();
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const phrases = [
    i18n.language === 'es' ? 'Moderniza tu Cafetería' :
    i18n.language === 'fr' ? 'Modernisez votre Café' :
    i18n.language === 'ar' ? 'حدّث مقهاك اليوم' :
    'Modernize Your Coffee Shop',
    
    i18n.language === 'es' ? 'Aumenta las Ventas' :
    i18n.language === 'fr' ? 'Augmentez les Ventes' :
    i18n.language === 'ar' ? 'زد المبيعات' :
    'Boost Your Sales',
    
    i18n.language === 'es' ? 'Encanta a los Clientes' :
    i18n.language === 'fr' ? 'Ravissez vos Clients' :
    i18n.language === 'ar' ? 'أسعد عملاءك' :
    'Delight Your Customers'
  ];

  useEffect(() => {
    if (prefersReducedMotion) {
      const id = setInterval(() => {
        setCurrentPhrase((prev) => (prev + 1) % phrases.length);
      }, 4000);
      return () => clearInterval(id);
    }

    const cycle = setInterval(() => {
      // Trigger exit animation
      setLeaving(true);
      // Wait for exit to finish, then swap phrase and enter
      const timeout = setTimeout(() => {
        setCurrentPhrase((prev) => (prev + 1) % phrases.length);
        setLeaving(false);
      }, 450); // match phrase-out duration in CSS
      return () => clearTimeout(timeout);
    }, 3600);

    return () => clearInterval(cycle);
  }, [phrases.length, prefersReducedMotion]);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Enhanced Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover pointer-events-none"
        >
          <source src="/videos/landing.mp4" type="video/mp4" />
        </video>
        
        {/* Multiple overlay layers for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-orange-900/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/20 via-transparent to-blue-900/20"></div>
      </div>

      {/* Floating Particles */}
      <FloatingParticles />

      {/* Main Content */}
      <div className="relative z-20 text-center px-6 max-w-6xl mx-auto">
        {/* Animated Badge */}
        <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 rounded-full px-6 py-3 mb-8 animate-fade-in-up">
          <Sparkles className="w-5 h-5 text-orange-400 animate-pulse" />
          <span className="text-orange-200 font-medium">
            {i18n.language === 'es' ? '¡Revoluciona tu Negocio!' :
             i18n.language === 'fr' ? 'Révolutionnez votre Business!' :
             i18n.language === 'ar' ? 'ثوّر عملك!' :
             'Revolutionize Your Business!'}
          </span>
        </div>

        {/* Animated Main Title */}
        <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight headline-smooth">
          <span
            className={`block bg-gradient-to-r from-white via-orange-200 to-orange-400 bg-clip-text text-transparent animate-gradient-x ${prefersReducedMotion ? '' : (leaving ? 'phrase-exit' : 'phrase-enter')}`}
            key={currentPhrase}
          >
            {phrases[currentPhrase]}
          </span>
        </h1>

        {/* Subtitle with Typewriter Effect */}
        <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed animate-fade-in-up-delay">
          {i18n.language === 'es' ? 'Menús QR, pedidos instantáneos y análisis en tiempo real. Todo lo que necesitas para aumentar ventas y encantar clientes.' :
           i18n.language === 'fr' ? 'Menus QR, commandes instantanées et analyses en temps réel. Tout ce qu\'il faut pour booster vos ventes et ravir vos clients.' :
           i18n.language === 'ar' ? 'قوائم QR، طلبات فورية، وتحليلات لحظية. كل ما تحتاجه لزيادة المبيعات وإسعاد العملاء.' :
           'QR menus, instant orders, and real-time analytics. Everything you need to boost sales and delight customers.'}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12 animate-fade-in-up-delay-2">
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className="group px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3"
              >
                <Coffee className="w-6 h-6" />
                {i18n.language === 'es' ? 'Mi Dashboard' :
                 i18n.language === 'fr' ? 'Mon Tableau de Bord' :
                 i18n.language === 'ar' ? 'لوحة التحكم' :
                 'My Dashboard'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={logout}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-bold text-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/20"
              >
                {i18n.language === 'es' ? 'Cerrar Sesión' :
                 i18n.language === 'fr' ? 'Se Déconnecter' :
                 i18n.language === 'ar' ? 'تسجيل الخروج' :
                 'Logout'}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/register"
                className="group px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3"
              >
                <Zap className="w-6 h-6" />
                {i18n.language === 'es' ? 'Prueba Gratuita' :
                 i18n.language === 'fr' ? 'Essai Gratuit' :
                 i18n.language === 'ar' ? 'تجربة مجانية' :
                 'Start Free Trial'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-bold text-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/20 flex items-center gap-3"
              >
                {i18n.language === 'es' ? 'Iniciar Sesión' :
                 i18n.language === 'fr' ? 'Se Connecter' :
                 i18n.language === 'ar' ? 'تسجيل الدخول' :
                 'Sign In'}
              </Link>
            </>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="flex justify-center items-center gap-8 text-gray-300 animate-fade-in-up-delay-3">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400 fill-current" />
            <span className="text-sm font-medium">
              {i18n.language === 'es' ? '4.9/5 Calificación' :
               i18n.language === 'fr' ? '4.9/5 Évaluation' :
               i18n.language === 'ar' ? '4.9/5 تقييم' :
               '4.9/5 Rating'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            <span className="text-sm font-medium">
              {i18n.language === 'es' ? 'Seguro SSL' :
               i18n.language === 'fr' ? 'SSL Sécurisé' :
               i18n.language === 'ar' ? 'آمن SSL' :
               'SSL Secure'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-medium">
              {i18n.language === 'es' ? '1000+ Cafeterías' :
               i18n.language === 'fr' ? '1000+ Cafés' :
               i18n.language === 'ar' ? '1000+ مقهى' :
               '1000+ Coffee Shops'}
            </span>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white/70" />
      </div>
    </div>
  );
};

// Stats section with animated counters
const StatsSection = () => {
  const { i18n } = useTranslation();

  return (
    <div className="py-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <AnimatedCounter end={1000} suffix="+" />
            <p className="text-gray-400 text-lg mt-2">
              {i18n.language === 'es' ? 'Cafeterías Activas' :
               i18n.language === 'fr' ? 'Cafés Actifs' :
               i18n.language === 'ar' ? 'مقهى نشط' :
               'Active Coffee Shops'}
            </p>
          </div>
          <div className="text-center">
            <AnimatedCounter end={50000} suffix="+" />
            <p className="text-gray-400 text-lg mt-2">
              {i18n.language === 'es' ? 'Órdenes Procesadas' :
               i18n.language === 'fr' ? 'Commandes Traitées' :
               i18n.language === 'ar' ? 'طلب معالج' :
               'Orders Processed'}
            </p>
          </div>
          <div className="text-center">
            <AnimatedCounter end={99} suffix="%" />
            <p className="text-gray-400 text-lg mt-2">
              {i18n.language === 'es' ? 'Tiempo de Actividad' :
               i18n.language === 'fr' ? 'Temps de Fonctionnement' :
               i18n.language === 'ar' ? 'وقت التشغيل' :
               'Uptime'}
            </p>
          </div>
          <div className="text-center">
            <AnimatedCounter end={24} suffix="/7" />
            <p className="text-gray-400 text-lg mt-2">
              {i18n.language === 'es' ? 'Soporte' :
               i18n.language === 'fr' ? 'Support' :
               i18n.language === 'ar' ? 'الدعم' :
               'Support'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced features section
const EnhancedFeaturesSection = () => {
  const { i18n } = useTranslation();

  const features = [
    {
      icon: QrCode,
      title: i18n.language === 'es' ? 'Menús QR Inteligentes' :
             i18n.language === 'fr' ? 'Menus QR Intelligents' :
             i18n.language === 'ar' ? 'قوائم QR ذكية' :
             'Smart QR Menus',
      description: i18n.language === 'es' ? 'Menús digitales interactivos con actualizaciones en tiempo real' :
                   i18n.language === 'fr' ? 'Menus numériques interactifs avec mises à jour en temps réel' :
                   i18n.language === 'ar' ? 'قوائم رقمية تفاعلية مع تحديثات فورية' :
                   'Interactive digital menus with real-time updates',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Smartphone,
      title: i18n.language === 'es' ? 'Pedidos Móviles' :
             i18n.language === 'fr' ? 'Commandes Mobiles' :
             i18n.language === 'ar' ? 'طلبات الهاتف المحمول' :
             'Mobile Ordering',
      description: i18n.language === 'es' ? 'Experiencia de pedido perfecta desde cualquier dispositivo' :
                   i18n.language === 'fr' ? 'Expérience de commande parfaite depuis n\'importe quel appareil' :
                   i18n.language === 'ar' ? 'تجربة طلب مثالية من أي جهاز' :
                   'Seamless ordering experience from any device',
      gradient: 'from-blue-500 to-purple-500'
    },
    {
      icon: BarChart3,
      title: i18n.language === 'es' ? 'Análisis Avanzados' :
             i18n.language === 'fr' ? 'Analyses Avancées' :
             i18n.language === 'ar' ? 'تحليلات متقدمة' :
             'Advanced Analytics',
      description: i18n.language === 'es' ? 'Obtén insights profundos sobre tu negocio' :
                   i18n.language === 'fr' ? 'Obtenez des insights approfondis sur votre entreprise' :
                   i18n.language === 'ar' ? 'احصل على رؤى عميقة حول عملك' :
                   'Get deep insights into your business performance',
      gradient: 'from-green-500 to-teal-500'
    }
  ];

  return (
    <div className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-white via-orange-200 to-orange-400 bg-clip-text text-transparent">
              {i18n.language === 'es' ? 'Características Potentes' :
               i18n.language === 'fr' ? 'Fonctionnalités Puissantes' :
               i18n.language === 'ar' ? 'ميزات قوية' :
               'Powerful Features'}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {i18n.language === 'es' ? 'Todo lo que necesitas para modernizar tu cafetería y aumentar tus ventas' :
             i18n.language === 'fr' ? 'Tout ce dont vous avez besoin pour moderniser votre café et augmenter vos ventes' :
             i18n.language === 'ar' ? 'كل ما تحتاجه لتحديث مقهاك وزيادة مبيعاتك' :
             'Everything you need to modernize your coffee shop and boost your sales'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative p-8 bg-gray-800/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 hover:border-orange-500/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`}></div>
              
              {/* Icon */}
              <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-300 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>

              {/* Hover Effect */}
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transform -skew-x-12 translate-x-full group-hover:translate-x-0 transition-all duration-700"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Landing Component
const Landing = () => {
  const { isAuthenticated, user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <TransparentHeader />
      
      <EnhancedHeroSection 
        isAuthenticated={isAuthenticated} 
        user={user} 
        logout={logout} 
      />
      
      <StatsSection />
      
      <EnhancedFeaturesSection />
      
      {/* Additional sections can be added here */}
    </div>
  );
};

export default Landing;
